import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link, useLocation } from 'react-router-dom';
import {
  FiActivity as Activity,
  FiCpu as Cpu,
  FiAlertTriangle as AlertTriangle,
  FiSettings as Settings,
  FiRefreshCw as RefreshCw,
  FiDatabase as Database,
  FiCheckCircle as CheckCircle,
} from 'react-icons/fi';
import {
  getAPMMetricsSummary,
  getAPMMetricsTimeseries,
  getAPMHealth,
  getAPMAlerts,
  acknowledgeAPMAlert,
  resolveAPMAlert,
} from '../../../../services/aiOperationsApi';
import { APMTimeseriesChart, APMAlertRulesTable, APMAlertRuleModal, MemoryLeakDetectionCard } from '../components/apm';
import type { AlertRule as TableAlertRule } from '../components/apm/APMAlertRulesTable';
import type { AlertRule as ModalAlertRule } from '../components/apm/APMAlertRuleModal';
import useAPMAlertRules from '../../../../hooks/useAPMAlertRules';
import DateRangeFilter from '../../../../components/dashboard/DateRangeFilter';
import { dateRangeToParams } from '../../../../components/dashboard/widgetDataFetcher';
import { useProject } from '../../../../contexts/ProjectContext';
import { useFilter } from '../../../../contexts/FilterContext';
import MemoryPage from './MemoryPage';

interface APMMemoryMetrics {
  current_heap_percent?: number;
  current_heap_used?: number;
  current_heap_total?: number;
  current_heap_max?: number;
  current_rss?: number;
  avg_heap_percent?: number;
  max_heap_percent?: number;
}

interface APMCpuMetrics {
  current_cpu_percent?: number;
  avg_cpu_percent?: number;
  max_cpu_percent?: number;
}

interface APMThreadMetrics {
  current_count?: number;
  current_active?: number;
  current_blocked?: number;
  current_waiting?: number;
  avg_count?: number;
}

interface APMSummary {
  memory?: APMMemoryMetrics;
  cpu?: APMCpuMetrics;
  threads?: APMThreadMetrics;
  agents?: string[];
  metrics_count?: number;
}

interface APMDataPoint {
  timestamp: string | number;
  value?: number | null;
  avg_value?: number | null;
  [key: string]: unknown;
}

const TABS = [
  { id: 'overview', label: 'Overview', icon: Activity },
  { id: 'memory', label: 'Memory', icon: Database },
  { id: 'cpu', label: 'CPU & Threads', icon: Cpu },
  { id: 'alerts', label: 'Alerts', icon: AlertTriangle },
  { id: 'settings', label: 'Settings', icon: Settings },
];

// Default presets for APM (hourly + daily options)
const APM_PRESETS = ['last_1_hour', 'last_6_hours', 'last_24_hours', 'last_7_days', 'last_30_days', 'last_90_days', 'this_year'];

// Map URL range values to preset keys for URL compatibility
const urlRangeToPreset = (range: string) => {
  const mapping: Record<string, string> = {
    '1h': 'last_1_hour',
    '6h': 'last_6_hours',
    '24h': 'last_24_hours',
    '7d': 'last_7_days',
    '30d': 'last_30_days',
    '90d': 'last_90_days',
    'this_year': 'this_year',
  };
  return mapping[range] || 'last_1_hour';
};

const presetToUrlRange = (preset: string) => {
  const mapping: Record<string, string> = {
    'last_1_hour': '1h',
    'last_6_hours': '6h',
    'last_24_hours': '24h',
    'last_7_days': '7d',
    'last_30_days': '30d',
    'last_90_days': '90d',
    'this_year': 'this_year',
  };
  return mapping[preset] || '1h';
};

// Health Score Circle Component
const HealthScoreCircle = ({ score, size = 120, label }: { score: number | null | undefined; size?: number; label?: string }) => {
  const radius = (size - 10) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = score == null ? 0 : (score / 100) * circumference;

  const getColor = (score: number | null | undefined) => {
    if (score == null) return '#6b7280';
    if (score >= 80) return '#10b981';
    if (score >= 60) return '#f59e0b';
    if (score >= 40) return '#f97316';
    return '#ef4444';
  };

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth="8"
          fill="none"
          className="text-gray-200 dark:text-gray-700"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={getColor(score)}
          strokeWidth="8"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference - progress}
          className="transition-all duration-500"
        />
      </svg>
      <div className="absolute flex flex-col items-center justify-center" style={{ width: size, height: size }}>
        <span className="text-3xl font-bold text-gray-900 dark:text-white">
          {score ?? '--'}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">{label || 'Health'}</span>
      </div>
    </div>
  );
};

// Metric Card Component
const MetricCard = ({ title, value, unit, trend, status, icon: Icon }: { title: string; value: string | number | null | undefined; unit?: string; trend?: number; status?: string | null; icon?: React.ComponentType<{ className?: string }> }) => {
  const getStatusColor = () => {
    if (status === 'good') return 'text-green-500';
    if (status === 'warning') return 'text-yellow-500';
    if (status === 'critical') return 'text-red-500';
    return 'text-gray-500';
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-gray-500 dark:text-gray-400">{title}</span>
        {Icon && <Icon className={`w-5 h-5 ${getStatusColor()}`} />}
      </div>
      <div className="flex items-baseline gap-1">
        <span className="text-2xl font-bold text-gray-900 dark:text-white">
          {value ?? '--'}
        </span>
        {unit && <span className="text-sm text-gray-500 dark:text-gray-400">{unit}</span>}
      </div>
      {trend && (
        <div className={`text-xs mt-1 ${trend > 0 ? 'text-red-500' : 'text-green-500'}`}>
          {trend > 0 ? '+' : ''}{trend}% from average
        </div>
      )}
    </div>
  );
};


// Alert Badge Component
interface APMAlert {
  id: string;
  title: string;
  description?: string;
  severity: string;
  status: string;
  agent_name?: string;
  triggered_at: string;
  actual_value?: number | null;
  threshold_value?: number;
}

const AlertBadge = ({ alert, onAcknowledge, onResolve }: { alert: APMAlert; onAcknowledge?: (id: string) => void; onResolve?: (id: string) => void }) => {
  const getSeverityColor = () => {
    if (alert.severity === 'critical') return 'bg-red-100 border-red-500 text-red-800 dark:bg-red-900/30 dark:text-red-300';
    return 'bg-yellow-100 border-yellow-500 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
  };

  return (
    <div className={`border-l-4 rounded-r-lg p-4 ${getSeverityColor()}`}>
      <div className="flex items-start justify-between">
        <div>
          <h4 className="font-medium">{alert.title}</h4>
          <p className="text-sm mt-1 opacity-80">{alert.description}</p>
          <div className="flex items-center gap-4 mt-2 text-xs">
            <span>Agent: {alert.agent_name || 'All'}</span>
            <span>Triggered: {new Date(alert.triggered_at).toLocaleString()}</span>
            {alert.actual_value != null && (
              <span>Value: {alert.actual_value.toFixed(2)} (threshold: {alert.threshold_value})</span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {alert.status === 'active' && (
            <>
              <button
                onClick={() => onAcknowledge?.(alert.id)}
                className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Acknowledge
              </button>
              <button
                onClick={() => onResolve?.(alert.id)}
                className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
              >
                Resolve
              </button>
            </>
          )}
          {alert.status === 'acknowledged' && (
            <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded dark:bg-blue-900/30 dark:text-blue-300">
              Acknowledged
            </span>
          )}
          {alert.status === 'resolved' && (
            <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded dark:bg-green-900/30 dark:text-green-300">
              Resolved
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const APMPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedProjectId } = useProject();
  const { filterType, selectedId: filterId } = useFilter();

  // State - use DateRangeFilter format
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [selectedAgent, setSelectedAgent] = useState(searchParams.get('agent') || '');
  const initialPreset = urlRangeToPreset(searchParams.get('range') || '1h');
  const [dateRange, setDateRange] = useState<{ preset: string; startDate: string | null; endDate: string | null }>({ preset: initialPreset, startDate: null, endDate: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Data
  const [summary, setSummary] = useState<APMSummary | null>(null);
  const [availableAgents, setAvailableAgents] = useState<string[]>([]);
  const [heapTimeseries, setHeapTimeseries] = useState<APMDataPoint[]>([]);
  const [cpuTimeseries, setCpuTimeseries] = useState<APMDataPoint[]>([]);
  const [health, setHealth] = useState<{ score?: number; status?: string } | null>(null);
  const [alerts, setAlerts] = useState<APMAlert[]>([]);

  // Alert Rules
  const {
    rules: alertRules,
    loading: rulesLoading,
    saving: rulesSaving,
    createRule,
    updateRule,
    deleteRule,
    toggleRule,
    seedDefaults,
  } = useAPMAlertRules();
  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<ModalAlertRule | null>(null);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = dateRangeToParams(dateRange);
      const timeRange = params.timeRange; // APM-compatible format (1h, 6h, 7d, etc.)

      const [summaryRes, unfilteredSummaryRes, heapRes, cpuRes, healthRes, alertsRes] = await Promise.all([
        getAPMMetricsSummary({
          timeRange,
          agentName: selectedAgent || undefined,
          projectId: selectedProjectId,
        }),
        selectedAgent
          ? getAPMMetricsSummary({ timeRange, projectId: selectedProjectId })
          : Promise.resolve(null),
        getAPMMetricsTimeseries({
          metric: 'heap_percent',
          timeRange,
          interval: '5m',
          agentName: selectedAgent || undefined,
          projectId: selectedProjectId,
        }),
        getAPMMetricsTimeseries({
          metric: 'cpu_usage',
          timeRange,
          interval: '5m',
          agentName: selectedAgent || undefined,
          projectId: selectedProjectId,
        }),
        getAPMHealth({
          timeRange,
          agentName: selectedAgent || undefined,
          projectId: selectedProjectId,
        }),
        getAPMAlerts({ status: 'active', limit: 10, agentName: selectedAgent || undefined }),
      ]);

      setSummary(summaryRes);
      setAvailableAgents(((unfilteredSummaryRes ?? summaryRes)?.agents || []) as string[]);
      setHeapTimeseries(heapRes?.data || []);
      setCpuTimeseries(cpuRes?.data || []);
      setHealth(healthRes);
      setAlerts(alertsRes?.data || []);
    } catch (err: unknown) {
      console.error('APM data fetch error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load APM data');
    } finally {
      setLoading(false);
    }
  }, [dateRange, selectedAgent, selectedProjectId, filterType, filterId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Sync URL
  useEffect(() => {
    if (isMemorySection) return;
    const params = new URLSearchParams();
    if (activeTab !== 'overview') params.set('tab', activeTab);
    const urlRange = presetToUrlRange(dateRange.preset);
    if (urlRange !== '1h') params.set('range', urlRange);
    if (selectedAgent) params.set('agent', selectedAgent);
    setSearchParams(params, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, dateRange, selectedAgent]);

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
  };

  const getMemoryStatus = () => {
    const heapPercent = summary?.memory?.current_heap_percent;
    if (heapPercent == null) return null;
    if (heapPercent < 60) return 'good';
    if (heapPercent < 80) return 'warning';
    return 'critical';
  };

  const getCpuStatus = () => {
    const cpuPercent = summary?.cpu?.current_cpu_percent;
    if (cpuPercent == null) return null;
    if (cpuPercent < 70) return 'good';
    if (cpuPercent < 90) return 'warning';
    return 'critical';
  };

  const formatBytes = (bytes: number | null | undefined) => {
    if (bytes == null) return '--';
    if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(2)} GB`;
    if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(2)} MB`;
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${bytes} B`;
  };

  // Top-level section: performance vs memory (determined by route path)
  const { pathname } = useLocation();
  const isMemorySection = pathname.includes('/memory');

  if (isMemorySection) {
    return (
      <>
        <div className="mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex space-x-8">
              <Link to="/ai-operations/apm" className="py-3 px-1 border-b-2 font-medium text-sm transition-colors border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300">
                Performance
              </Link>
              <span className="py-3 px-1 border-b-2 font-medium text-sm border-blue-500 text-blue-600 dark:text-blue-400">
                Memory
              </span>
            </nav>
          </div>
        </div>
        <MemoryPage />
      </>
    );
  }

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Section tabs */}
      <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          <span className="py-3 px-1 border-b-2 font-medium text-sm border-blue-500 text-blue-600 dark:text-blue-400">
            Performance
          </span>
          <Link to="/ai-operations/memory" className="py-3 px-1 border-b-2 font-medium text-sm transition-colors border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300">
            Memory
          </Link>
        </nav>
      </div>

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white transition-colors">
              Application Performance Monitoring
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400 transition-colors">
              Monitor agent memory, CPU, threads, and system health
            </p>
          </div>

          <div className="flex items-center space-x-3">
            <select
              value={selectedAgent}
              onChange={(e) => setSelectedAgent(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              aria-label="Filter APM by agent"
            >
              <option value="">All agents</option>
              {availableAgents.map((agent) => (
                <option key={agent} value={agent}>{agent}</option>
              ))}
            </select>
            {/* Time Range Selector */}
            <DateRangeFilter
              value={dateRange}
              onChange={setDateRange}
              compact={true}
              presets={APM_PRESETS}
            />

            {/* Refresh Button */}
            <button
              onClick={fetchData}
              disabled={loading}
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700
                       hover:bg-gray-200 dark:hover:bg-gray-600
                       disabled:opacity-50 transition-colors"
              title="Refresh data"
            >
              <RefreshCw className={`w-5 h-5 text-gray-600 dark:text-gray-300 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Active Alerts Banner */}
      {alerts.length > 0 && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mr-3" />
            <div>
              <h3 className="font-medium text-red-800 dark:text-red-300">
                {alerts.length} Active Alert{alerts.length > 1 ? 's' : ''}
              </h3>
              <p className="text-sm text-red-700 dark:text-red-400">
                {alerts[0]?.title} {alerts.length > 1 && `and ${alerts.length - 1} more`}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow transition-colors">
        {/* Tabs Navigation */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-8 px-6 overflow-x-auto" aria-label="Tabs">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {tab.label}
                  {tab.id === 'alerts' && alerts.length > 0 && (
                    <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
                      {alerts.length}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Loading State */}
          {loading && !summary && (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          )}

          {/* Error State */}
          {error && !summary && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex items-center">
                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
                <p className="text-red-800 dark:text-red-300">Error loading APM data: {error}</p>
              </div>
              <button
                onClick={fetchData}
                className="mt-3 text-sm text-red-600 dark:text-red-400 hover:underline"
              >
                Try again
              </button>
            </div>
          )}

          {/* Overview Tab */}
          {activeTab === 'overview' && summary && (
            <div className="space-y-6">
              {/* Health Score Row */}
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                <div className="lg:col-span-1 flex justify-center">
                  <div className="relative">
                    <HealthScoreCircle score={health?.score} label={health?.status || 'Health'} />
                  </div>
                </div>

                <div className="lg:col-span-3 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <MetricCard
                    title="Heap Usage"
                    value={summary.memory?.current_heap_percent?.toFixed(1)}
                    unit="%"
                    status={getMemoryStatus()}
                    icon={Database}
                  />
                  <MetricCard
                    title="RSS Memory"
                    value={formatBytes(summary.memory?.current_rss)}
                    status={getMemoryStatus()}
                    icon={Database}
                  />
                  <MetricCard
                    title="CPU Usage"
                    value={summary.cpu?.current_cpu_percent?.toFixed(1)}
                    unit="%"
                    status={getCpuStatus()}
                    icon={Cpu}
                  />
                </div>
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <APMTimeseriesChart
                  data={heapTimeseries}
                  title="Heap Memory Usage (%)"
                  unit="%"
                  color="#3b82f6"
                  warningThreshold={60}
                  criticalThreshold={80}
                  height={200}
                />
                <APMTimeseriesChart
                  data={cpuTimeseries}
                  title="CPU Usage (%)"
                  unit="%"
                  color="#10b981"
                  warningThreshold={70}
                  criticalThreshold={90}
                  height={200}
                />
              </div>

              {/* Additional Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <MetricCard
                  title="Thread Count"
                  value={summary.threads?.current_count}
                  icon={Cpu}
                />
                <MetricCard
                  title="Active Threads"
                  value={summary.threads?.current_active}
                  icon={Cpu}
                />
              </div>

              {/* Agents List */}
              {availableAgents.length > 0 && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Active Agents ({availableAgents.length})
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {availableAgents.map((agent: string) => (
                      <button
                        type="button"
                        key={agent}
                        onClick={() => setSelectedAgent((current) => current === agent ? '' : agent)}
                        className={`px-3 py-1 rounded-full text-sm ${
                          selectedAgent === agent
                            ? 'bg-blue-600 text-white dark:bg-blue-500'
                            : 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'
                        }`}
                      >
                        {agent}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Memory Tab */}
          {activeTab === 'memory' && summary && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <MetricCard
                  title="Current Heap"
                  value={formatBytes(summary.memory?.current_heap_used)}
                  status={getMemoryStatus()}
                  icon={Database}
                />
                <MetricCard
                  title="Heap Total"
                  value={formatBytes(summary.memory?.current_heap_total)}
                  icon={Database}
                />
                <MetricCard
                  title="Max Heap"
                  value={formatBytes(summary.memory?.current_heap_max)}
                  icon={Database}
                />
                <MetricCard
                  title="RSS"
                  value={formatBytes(summary.memory?.current_rss)}
                  icon={Database}
                />
              </div>

              <APMTimeseriesChart
                data={heapTimeseries}
                title="Heap Memory Usage Over Time (%)"
                unit="%"
                color="#3b82f6"
                warningThreshold={60}
                criticalThreshold={80}
                height={250}
              />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <MetricCard
                  title="Avg Heap Usage"
                  value={summary.memory?.avg_heap_percent?.toFixed(1)}
                  unit="%"
                />
                <MetricCard
                  title="Max Heap Usage"
                  value={summary.memory?.max_heap_percent?.toFixed(1)}
                  unit="%"
                />
                <MetricCard
                  title="Data Points"
                  value={summary.metrics_count}
                />
              </div>

              {/* Memory Leak Detection */}
              <MemoryLeakDetectionCard
                agentName={selectedAgent}
                projectId={selectedProjectId}
              />
            </div>
          )}

          {/* CPU Tab */}
          {activeTab === 'cpu' && summary && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <MetricCard
                  title="Current CPU"
                  value={summary.cpu?.current_cpu_percent?.toFixed(1)}
                  unit="%"
                  status={getCpuStatus()}
                  icon={Cpu}
                />
                <MetricCard
                  title="Avg CPU"
                  value={summary.cpu?.avg_cpu_percent?.toFixed(1)}
                  unit="%"
                  icon={Cpu}
                />
                <MetricCard
                  title="Max CPU"
                  value={summary.cpu?.max_cpu_percent?.toFixed(1)}
                  unit="%"
                  icon={Cpu}
                />
                <MetricCard
                  title="Thread Count"
                  value={summary.threads?.current_count}
                  icon={Cpu}
                />
              </div>

              <APMTimeseriesChart
                data={cpuTimeseries}
                title="CPU Usage Over Time (%)"
                unit="%"
                color="#10b981"
                warningThreshold={70}
                criticalThreshold={90}
                height={250}
              />

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <MetricCard
                  title="Active Threads"
                  value={summary.threads?.current_active}
                />
                <MetricCard
                  title="Blocked Threads"
                  value={summary.threads?.current_blocked}
                />
                <MetricCard
                  title="Waiting Threads"
                  value={summary.threads?.current_waiting}
                />
                <MetricCard
                  title="Avg Thread Count"
                  value={summary.threads?.avg_count?.toFixed(0)}
                />
              </div>
            </div>
          )}

          {/* Alerts Tab */}
          {activeTab === 'alerts' && (
            <div className="space-y-4">
              {alerts.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">No Active Alerts</h3>
                  <p className="text-gray-500 dark:text-gray-400">All systems are operating normally</p>
                </div>
              ) : (
                alerts.map((alert) => (
                  <AlertBadge
                    key={alert.id}
                    alert={alert}
                    onAcknowledge={async (id) => {
                      try {
                        await acknowledgeAPMAlert(id, 'current_user');
                        fetchData();
                      } catch (err: unknown) {
                        console.error('Failed to acknowledge alert:', err);
                      }
                    }}
                    onResolve={async (id) => {
                      try {
                        await resolveAPMAlert(id);
                        fetchData();
                      } catch (err: unknown) {
                        console.error('Failed to resolve alert:', err);
                      }
                    }}
                  />
                ))
              )}
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              {/* Header with actions */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Alert Rules Configuration
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Configure alert thresholds for memory, RSS, CPU, thread, and leak metrics.
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={seedDefaults}
                    disabled={rulesSaving}
                    className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700
                      border border-gray-300 dark:border-gray-600 rounded-lg
                      hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors
                      disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Seed Defaults
                  </button>
                  <button
                    onClick={() => {
                      setEditingRule(null);
                      setRuleModalOpen(true);
                    }}
                    className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg
                      hover:bg-blue-700 transition-colors"
                  >
                    Add Rule
                  </button>
                </div>
              </div>

              {/* Rules Table */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <APMAlertRulesTable
                  rules={alertRules as TableAlertRule[]}
                  loading={rulesLoading}
                  onEdit={(rule: TableAlertRule) => {
                    setEditingRule(rule as unknown as ModalAlertRule);
                    setRuleModalOpen(true);
                  }}
                  onDelete={async (ruleId) => {
                    if (globalThis.confirm('Are you sure you want to delete this rule?')) {
                      await deleteRule(ruleId);
                    }
                  }}
                  onToggle={toggleRule}
                />
              </div>

              {/* Alert Rule Modal */}
              <APMAlertRuleModal
                isOpen={ruleModalOpen}
                onClose={() => {
                  setRuleModalOpen(false);
                  setEditingRule(null);
                }}
                onSave={async (data, ruleId) => {
                  if (ruleId) {
                    return updateRule(ruleId, data);
                  }
                  return createRule(data);
                }}
                editingRule={editingRule}
                saving={rulesSaving}
              />
            </div>
          )}

          {/* No Data State */}
          {!loading && !error && !summary && (
            <div className="text-center py-12">
              <Activity className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">No APM Data</h3>
              <p className="text-gray-500 dark:text-gray-400 mt-2">
                Start sending APM metrics from your agents to see data here.
              </p>
              <pre className="mt-4 bg-gray-100 dark:bg-gray-700 rounded-lg p-4 text-left text-sm overflow-x-auto max-w-md mx-auto">
{`from aivilo import AiviloClient
from aivilo.apm import AiviloAPM

client = AiviloClient.from_env()
apm = AiviloAPM(client, "my-agent")
apm.start(interval_seconds=10)`}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default APMPage;
